import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragMoveEvent,
  type CollisionDetection,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { POI } from '@poi-planner/shared';
import { useTripStore } from '../store/tripStore';
import { useLibraryStore } from '../store/libraryStore';
import { useUIStore } from '../store/uiStore';
import { DayColumn } from './DayColumn';
import { Sidebar } from './Sidebar';
import { DragOverlayCard } from './DragOverlayCard';
import { POIEditorModal } from './POIEditorModal';
import { AccommodationConfigModal } from './AccommodationConfigModal';
import { TransportEditorModal } from './TransportEditorModal';
import { MapView } from './MapView';
import { useUndoKeyboard } from '../hooks/useUndoKeyboard';
import { useTripSidebarController } from '../hooks/useTripSidebarController';
import { dndIds } from '../utils/dndIds';
import { formatDate } from '../utils/format';
import type { MapBounds } from '../hooks/usePoiFilters';

interface DragData {
  type: 'sidebar-poi' | 'scheduled-poi';
  poiId?: string;
  dayId?: string;
}

function getBoundsForPois(pois: POI[]): MapBounds | null {
  const coords = pois.filter(
    (poi): poi is POI & { lat: number; lng: number } =>
      poi.lat != null && poi.lng != null && !isNaN(poi.lat) && !isNaN(poi.lng)
  );

  if (coords.length === 0) return null;

  if (coords.length === 1) {
    const poi = coords[0];
    const delta = 0.08;
    return {
      north: poi.lat + delta,
      south: poi.lat - delta,
      east: poi.lng + delta,
      west: poi.lng - delta,
    };
  }

  return coords.reduce<MapBounds>(
    (bounds, poi) => ({
      north: Math.max(bounds.north, poi.lat),
      south: Math.min(bounds.south, poi.lat),
      east: Math.max(bounds.east, poi.lng),
      west: Math.min(bounds.west, poi.lng),
    }),
    {
      north: coords[0].lat,
      south: coords[0].lat,
      east: coords[0].lng,
      west: coords[0].lng,
    }
  );
}

// Collision detection: prefer specific sortable cards over broad containers
const customCollisionDetection: CollisionDetection = (args) => {
  const pw = pointerWithin(args);
  if (pw.length > 0) {
    const cards = pw.filter((c) => dndIds.isDayItem(String(c.id)));
    if (cards.length > 0) return cards;
    return pw;
  }
  return closestCorners(args);
};

// Compute insertion index by comparing pointer Y with card DOM positions
function computeInsertIndex(dayId: string, pointerY: number): number {
  const state = useTripStore.getState();
  const day = state.schedule.days.find((d) => d.id === dayId);
  if (!day || day.scheduledPOIs.length === 0) return 0;

  const sorted = [...day.scheduledPOIs].sort((a, b) => a.order - b.order);

  for (let i = 0; i < sorted.length; i++) {
    const el = document.querySelector(`[data-poi-card="${dayId}:${sorted[i].poiId}"]`);
    if (el) {
      const rect = el.getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      if (pointerY < center) return i;
    }
  }

  return sorted.length;
}

export function DayFocusView() {
  const {
    trip,
    schedule,
    isReadOnly,
    getAccommodation,
    getPOI,
    getNightCount,
    schedulePOI,
    unschedulePOI,
    movePOI,
    reorderPOIs,
    deleteDay,
  } = useTripStore();

  const { pois, tags, getAccommodationPOIs } = useLibraryStore();
  const accommodationPOIs = getAccommodationPOIs();
  const accommodationConfigs = schedule.accommodationConfigs;

  const focusedDayId = useUIStore((s) => s.focusedDayId);
  const exitDayFocus = useUIStore((s) => s.exitDayFocus);
  const setFocusedDayId = useUIStore((s) => s.setFocusedDayId);
  const targetPoiId = useUIStore((s) => s.targetPoiId);
  const setTargetPoiId = useUIStore((s) => s.setTargetPoiId);
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);

  useUndoKeyboard();

  const [flyToTrigger, setFlyToTrigger] = useState(0);
  const [activeDragPOI, setActiveDragPOI] = useState<POI | null>(null);
  const pointerYRef = useRef(0);

  // Bounds filter state for sidebar
  const [boundsFilterEnabled, setBoundsFilterEnabled] = useState(false);
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);

  // Find the focused day
  const dayIndex = schedule.days.findIndex((d) => d.id === focusedDayId);
  const day = dayIndex >= 0 ? schedule.days[dayIndex] : null;
  const previousDay = dayIndex > 0 ? schedule.days[dayIndex - 1] : null;

  // Navigation
  const hasPrevDay = dayIndex > 0;
  const hasNextDay = dayIndex < schedule.days.length - 1;
  const prevDayId = hasPrevDay ? schedule.days[dayIndex - 1].id : null;
  const nextDayId = hasNextDay ? schedule.days[dayIndex + 1].id : null;

  // Get POIs scheduled for this day with coordinates
  const dayPois = useMemo(() => {
    if (!day) return [];
    return day.scheduledPOIs
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((sp) => getPOI(sp.poiId))
      .filter((p): p is POI => p != null);
  }, [day, getPOI]);

  // Route line for map
  const routeLine = useMemo(() => {
    const coordinates: [number, number][] = [];
    const poiIds: string[] = [];

    for (const poi of dayPois) {
      if (poi.lat != null && poi.lng != null) {
        coordinates.push([poi.lng, poi.lat]);
        poiIds.push(poi.id);
      }
    }

    return coordinates.length > 0 ? { coordinates, poiIds } : undefined;
  }, [dayPois]);

  const routeOrderByPoiId = useMemo(() => {
    const orderMap = new Map<string, number>();
    for (let i = 0; i < dayPois.length; i += 1) {
      orderMap.set(dayPois[i].id, i + 1);
    }
    return orderMap;
  }, [dayPois]);

  const sidebarController = useTripSidebarController(pois, schedule, {
    boundsFilterEnabled,
    mapBounds,
  });

  useEffect(() => {
    setBoundsFilterEnabled(true);
    setMapBounds(getBoundsForPois(dayPois));
  }, [focusedDayId, dayPois]);

  // Combined POIs for map: filtered sidebar POIs + day POIs (union)
  const mapPois = useMemo(() => {
    const combined = [...sidebarController.filteredPOIs];
    for (const poi of dayPois) {
      if (!sidebarController.filteredPOIs.some((p) => p.id === poi.id)) {
        combined.push(poi);
      }
    }
    return combined;
  }, [sidebarController.filteredPOIs, dayPois]);

  // Track pointer Y position continuously
  useEffect(() => {
    const handler = (e: PointerEvent) => {
      pointerYRef.current = e.clientY;
    };
    window.addEventListener('pointermove', handler, { passive: true });
    return () => window.removeEventListener('pointermove', handler);
  }, []);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
      disabled: isReadOnly(),
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      disabled: isReadOnly(),
    }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current as DragData | undefined;
    if (!data?.poiId) return;

    const poi = useTripStore.getState().getPOI(data.poiId);
    setActiveDragPOI(poi ?? null);
  }, []);

  const handleDragMove = useCallback(() => {
    // Could add insertion indicator logic here if needed
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragPOI(null);

    if (!over || !day) return;

    const activeData = active.data.current as DragData | undefined;
    if (!activeData?.poiId) return;

    const overId = String(over.id);

    // Dropped on sidebar -> unschedule
    if (overId === dndIds.sidebar) {
      if (activeData.type === 'scheduled-poi' && activeData.dayId) {
        unschedulePOI(activeData.poiId, activeData.dayId);
      }
      return;
    }

    // Determine if dropping on this day
    let targetDayId: string | null = null;
    if (dndIds.isDayContainer(overId)) {
      targetDayId = dndIds.parseDayContainer(overId);
    } else if (dndIds.isDayItem(overId)) {
      targetDayId = dndIds.parseDayItem(overId).dayId;
    }

    if (targetDayId !== day.id) return; // Only allow drops on focused day

    const targetIndex = computeInsertIndex(day.id, pointerYRef.current);

    if (activeData.type === 'sidebar-poi') {
      schedulePOI(activeData.poiId, day.id, targetIndex);
    } else if (activeData.type === 'scheduled-poi' && activeData.dayId) {
      if (activeData.dayId === day.id) {
        // Same day: reorder
        const sorted = [...day.scheduledPOIs].sort((a, b) => a.order - b.order);
        const oldIndex = sorted.findIndex((sp) => sp.poiId === activeData.poiId);
        if (oldIndex !== -1 && oldIndex !== targetIndex) {
          const adjustedTarget = targetIndex > oldIndex ? targetIndex - 1 : targetIndex;
          if (oldIndex !== adjustedTarget) {
            reorderPOIs(day.id, oldIndex, adjustedTarget);
          }
        }
      } else {
        // Different day: move
        movePOI(activeData.poiId, activeData.dayId, day.id, targetIndex);
      }
    }
  }, [day, schedulePOI, unschedulePOI, movePOI, reorderPOIs]);

  // Handle POI selection (bidirectional)
  const handleSelectPoi = useCallback((poiId: string | null) => {
    setTargetPoiId(poiId);
    if (poiId) {
      setFlyToTrigger((t) => t + 1);
    }
  }, [setTargetPoiId]);

  // Navigate to previous day
  const handlePrevDay = useCallback(() => {
    if (prevDayId) {
      setFocusedDayId(prevDayId);
    }
  }, [prevDayId, setFocusedDayId]);

  // Navigate to next day
  const handleNextDay = useCallback(() => {
    if (nextDayId) {
      setFocusedDayId(nextDayId);
    }
  }, [nextDayId, setFocusedDayId]);

  if (!trip || !day) {
    return null;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
    >
      <div className="flex flex-col h-screen w-full overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between whitespace-nowrap border-b border-border bg-white px-6 py-3 z-10 shrink-0">
          {/* Left: Back button + Day info */}
          <div className="flex items-center gap-4">
            <button
              onClick={exitDayFocus}
              className="btn-icon"
              title="Back to board"
            >
              <span className="material-symbols-outlined text-lg">arrow_back</span>
            </button>

            <div className="w-px h-6 bg-slate-200" />

            <button
              onClick={toggleSidebar}
              className="btn-icon"
              title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
            >
              <span className="material-symbols-outlined text-lg">
                {sidebarCollapsed ? 'right_panel_open' : 'left_panel_close'}
              </span>
            </button>

            <div className="w-px h-6 bg-slate-200" />

            {/* Day navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevDay}
                disabled={!hasPrevDay}
                className="btn-icon disabled:opacity-30 disabled:cursor-not-allowed"
                title="Previous day"
              >
                <span className="material-symbols-outlined text-lg">chevron_left</span>
              </button>

              <div className="text-center min-w-[140px]">
                <h1 className="text-lg font-bold text-slate-800 tracking-tight">
                  Day {dayIndex + 1}
                </h1>
                <p className="text-xs text-slate-500">
                  {formatDate(day.date)}
                </p>
              </div>

              <button
                onClick={handleNextDay}
                disabled={!hasNextDay}
                className="btn-icon disabled:opacity-30 disabled:cursor-not-allowed"
                title="Next day"
              >
                <span className="material-symbols-outlined text-lg">chevron_right</span>
              </button>
            </div>
          </div>

          {/* Right: Trip name + exit action */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-slate-400">{trip.name}</span>
            <button
              onClick={exitDayFocus}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
              title="Show full trip"
            >
              <span className="material-symbols-outlined text-base">view_kanban</span>
              <span>Show Full Trip</span>
            </button>
          </div>
        </header>

        {/* Main Content - Three columns */}
        <main className="flex flex-1 overflow-hidden bg-slate-100">
          {/* Sidebar */}
          {!sidebarCollapsed && (
            <Sidebar
              controller={sidebarController}
              onSelectPoi={handleSelectPoi}
              boundsFilterEnabled={boundsFilterEnabled}
              onBoundsFilterChange={setBoundsFilterEnabled}
            />
          )}

          {/* Day Column */}
          <div className="w-[352px] shrink-0 px-4 py-4 bg-slate-100 overflow-y-auto custom-scrollbar">
            <DayColumn
              day={day}
              dayIndex={dayIndex}
              previousDay={previousDay}
              accommodationPOIs={accommodationPOIs}
              accommodationConfigs={accommodationConfigs}
              pois={pois}
              transportEvents={schedule.transportEvents}
              getAccommodation={getAccommodation}
              getPOI={getPOI}
              getNightCount={getNightCount}
              onDeleteDay={deleteDay}
              insertIndicator={null}
              poiOrderById={routeOrderByPoiId}
              selectedPoiId={targetPoiId}
              onSelectPoi={handleSelectPoi}
            />
          </div>

          {/* Map - fills remaining space */}
          <div className="flex-1 flex flex-col py-4 pr-4 pl-0 bg-slate-100 min-w-0">
            <MapView
              pois={mapPois}
              tags={tags}
              selectedPoiId={targetPoiId}
              flyToTrigger={flyToTrigger}
              onSelectPoi={handleSelectPoi}
              onBoundsChange={setMapBounds}
              routeLine={routeLine}
              routeOrderByPoiId={routeOrderByPoiId}
              fitToPois={dayPois}
              fitToPoisKey={focusedDayId ?? undefined}
            />
          </div>
        </main>
      </div>

      <DragOverlay dropAnimation={null}>
        {activeDragPOI ? <DragOverlayCard poi={activeDragPOI} /> : null}
      </DragOverlay>

      <POIEditorModal />
      <AccommodationConfigModal />
      <TransportEditorModal />
    </DndContext>
  );
}
